import React, { useContext, useState } from 'react';
import { Heading5, Link } from '@qover/typography';
import { useDocumentLink } from '@qover/hooks';
import { RefsContext } from '@qover/contexts';
// @ts-ignore
import { ThemeContext } from '@qover/styles';
import Config from '@qover/utils/src/Config';
import { PdfsService } from '@qover/services';
import { Trans } from '@lingui/react';
import { DocumentPreviewModal } from '../modals';
import { Container, LinkContainer } from './documentInformation.styles';
import { CSSObject } from 'styled-components';

const DocumentIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
  >
    <defs>
      <path
        id="prefix__a"
        d="M15.5 0a.5.5 0 01.5.5v19a.5.5 0 01-.5.5H.5a.5.5 0 01-.5-.5V.5A.5.5 0 01.5 0h15zM7.75 14h-3.5a.75.75 0 100 1.5h3.5a.75.75 0 100-1.5zm4-2.5h-7.5a.75.75 0 100 1.5h7.5a.75.75 0 100-1.5zm-4-2.5h-3.5a.75.75 0 000 1.5h3.5a.75.75 0 100-1.5zm4-2.5h-7.5a.75.75 0 000 1.5h7.5a.75.75 0 100-1.5zm0-2.5h-7.5a.75.75 0 000 1.5h7.5a.75.75 0 100-1.5z"
      />
    </defs>
    <g fill="none" fillRule="evenodd" transform="translate(2)">
      <mask id="prefix__b" fill="#fff">
        <use xlinkHref="#prefix__a" />
      </mask>
      <use xlinkHref="#prefix__a" />
      <g mask="url(#prefix__b)">
        <path d="M0 0H20V20H0z" transform="translate(-2)" />
      </g>
    </g>
  </svg>
);

const LinkIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18">
    <defs>
      <path
        id="a"
        d="M10 0a9 9 0 11-8.588 11.7h1.912a7.2 7.2 0 10-.001-5.4H1.412C2.559 2.65 5.97 0 10 0zM8.755 5.033a.932.932 0 011.297 0L13.367 8.4c.35.35.35.947 0 1.298l-3.315 3.314a.932.932 0 01-1.297 0 .925.925 0 01-.263-.649c0-.228.087-.473.263-.649l1.788-1.788H1.906a.906.906 0 01-.912-.912c0-.509.403-.912.912-.912h8.585L8.755 6.33a.932.932 0 010-1.298z"
      />
    </defs>
    <g transform="translate(-1)" fill="none" fillRule="evenodd">
      <mask id="b" fill="#fff">
        <use xlinkHref="#a" />
      </mask>
      <use fill="#50565B" xlinkHref="#a" />
      <g mask="url(#b)" style={{ fill: 'var(--color-primary-100)' }}>
        <path d="M1 0h18v18H1z" />
      </g>
    </g>
  </svg>
);

const messages = {
  documentTitle: <Trans id="summary.documentsCard.documentTitle" />,
  generalConditions: <Trans id="generalConditions" />,
  ipid: <Trans id="ipid" />,
  preview: <Trans id="preview" />,
  resume: <Trans id="resume" />,
  warranties: <Trans id="warranties" />,
};

type DocumentInformationProps = {
  createDraftPreview: (draftId: string) => Promise<void>;
  createFipPreview: (draftId: string) => Promise<void>;
  draftId: string;
  generalConditions?: string;
  ipid?: string;
  showContractPreview?: boolean;
  showFipPreview?: boolean;
  showWarrantiesLink?: boolean;
  containerStyle?: React.CSSProperties;
};

export function DocumentInformation(props: DocumentInformationProps) {
  const { ipid = 'IPID', generalConditions = 'generalConditions' } = props;
  const refs = useContext(RefsContext);
  const theme = useContext<any>(ThemeContext);
  const [showContractPreviewModal, setShowContractPreviewModal] = useState(
    false,
  );
  const [showFipPreviewModal, setShowFipPreviewModal] = useState(false);
  const getLink = useDocumentLink();

  return (
    <Container id="documents" className="rounded" style={props.containerStyle}>
      <Heading5 as="div">{messages.documentTitle}</Heading5>

      {props.showContractPreview &&
        theme?.featureToggles?.documents?.showContractPreview && (
          <LinkContainer>
            <DocumentIcon />
            <Link
              className="font-bold"
              href=""
              onClick={e => {
                e.preventDefault();
                setShowContractPreviewModal(true);
              }}
              title="Preview"
            >
              {messages.preview}
            </Link>
          </LinkContainer>
        )}
      <LinkContainer>
        <DocumentIcon />
        <Link
          className="font-bold"
          href={getLink(ipid)}
          rel="noopener noreferrer"
          target="_blank"
          title="IPID"
        >
          {messages.ipid}
        </Link>
      </LinkContainer>
      <LinkContainer>
        <DocumentIcon />
        <Link
          className="font-bold"
          href={getLink(generalConditions)}
          rel="noopener noreferrer"
          target="_blank"
          title="General conditions"
        >
          {messages.generalConditions}
        </Link>
      </LinkContainer>
      {props.showFipPreview &&
        theme?.featureToggles?.documents?.showContractFIP && (
          <LinkContainer>
            <DocumentIcon />
            <Link
              className="font-bold"
              href=""
              onClick={e => {
                e.preventDefault();
                setShowFipPreviewModal(true);
              }}
              title="Resume"
            >
              {messages.resume}
            </Link>
          </LinkContainer>
        )}
      {props.showWarrantiesLink && (
        <LinkContainer>
          <LinkIcon />
          <Link
            className="text-primary font-bold"
            href="#insurance-details"
            title="view warranties"
          >
            {messages.warranties}
          </Link>
        </LinkContainer>
      )}
      {theme?.featureToggles?.documents?.showContractPreview &&
        showContractPreviewModal && (
          <DocumentPreviewModal
            createDocumentPreview={() =>
              props.createDraftPreview(props.draftId)
            }
            downloadUrl={`${Config.getApiUrl('pdf')}/v1/download?product=${
              refs.productRef
            }&type=TEMPLATE_TYPE_DRAFT_PREVIEW&country=${
              refs.country
            }&documentReference=${props.draftId}&apikey=${refs.apikey}`}
            pollDocumentPreview={() =>
              PdfsService.pollDraftPreview(
                props.draftId,
                refs.productRef,
                refs.country,
              )
            }
            onClose={() => setShowContractPreviewModal(false)}
          />
        )}
      {theme?.featureToggles?.documents?.showContractFIP &&
        showFipPreviewModal && (
          <DocumentPreviewModal
            createDocumentPreview={() => props.createFipPreview(props.draftId)}
            downloadUrl={`${Config.getApiUrl('pdf')}/v1/download?product=${
              refs.productRef
            }&type=TEMPLATE_TYPE_NEED_ANALYSIS&country=${
              refs.country
            }&documentReference=${props.draftId}&apikey=${refs.apikey}`}
            pollDocumentPreview={() =>
              PdfsService.pollFipPreview(
                props.draftId,
                refs.productRef,
                refs.country,
              )
            }
            onClose={() => setShowFipPreviewModal(false)}
          />
        )}
    </Container>
  );
}
